import {
  ITaskResponse,
  SignatureType,
  TaskApprovalType,
  TaskResponseType,
} from "@evennt-tech/api-v2-core";
import { IGridSchema } from "ts/api/grid-schema";
import {
  IApproveTaskInstanceBodyReq,
  IGetOneByIdTaskOption,
  IRejectTaskInstanceBodyReq,
  IReviewerDecisionTaskInstanceBodyReq,
  IReviewerDecisionTaskInstanceRes,
  IReviseTaskInstanceBodyReq,
  ISubmitTaskInstanceBodyReq,
  ISubmitTaskInstanceRes,
  ISubmitTaskInstanceSignatureInput,
} from "@evennt-tech/api-v2-external";
import { v4 as uuid } from "uuid";
import { apiV2 } from "api/firebase";
import {
  IMAGE_COMPRESSION_MAX_SIZE_MB,
  compressImage,
} from "tools/file/compressImage";
import { AttachmentFile, RawAttachmentFile } from "ts/api/attachmentFile";
import { Grid as TaskGrid, GridForm } from "ts/api/task";
import { formAnswerToSubmitInput } from "tools/task/grid/mappers";

/**
 * apiV2 helpers for every WRITE on a task instance from the exhibitor /
 * organiser drawer flow. The BE owns status transitions, history rows,
 * notifications, multi-reviewer reset, linked-custom-field sync, and
 * follower email fan-out — submit handlers must not write timeline rows
 * themselves. MULTIPLE_CHOICE / CHECKBOXES form state carries option
 * KEYs, which these helpers translate to `task_options.id` UUIDs (one
 * deep-task fetch per submit, cached by react-query upstream).
 */

export interface ApiV2TaskContext {
  showId: string;
  standId: string;
  /** Required when the caller's role is EXHIBITOR (ACL guard). */
  exhibitorPairId?: string;
}

/**
 * Context for the response-writing calls (submit / draft / not-applicable).
 * A stand can host several exhibitor companies, so an ANSWER is always given
 * in the context of one pair: the BE requires `exhibitorPairId` on
 * `POST /task/:taskId/instance` and persists it as
 * `task_instances.respondent_exhibitor_pair_id` (the attribution every
 * consumer — verdict emails, Dashboard timeline, PDFs, reports — reads instead
 * of guessing a company from the stand). Read-only / verdict calls keep the
 * looser `ApiV2TaskContext`.
 */
export type ApiV2TaskSubmitContext = ApiV2TaskContext & {
  exhibitorPairId: string;
};

/**
 * In-progress response form data. Per-type fields are mutually
 * exclusive — only the one matching `responseType` is populated.
 */
export interface LegacyResponseFormData {
  shortAnswer?: string;
  shortAnswerRows?: string[];
  paragraph?: string;
  multipleChoice?: string;
  /**
   * Two-tier MULTIPLE_CHOICE selection: `{[parentOptionKey]:
   * nestedOptionKey}`. Submit collapses to the deepest pick since the
   * column stores a single option id; the renderer walks `parentOptionId`
   * to surface the parent breadcrumb.
   */
  multipleChoiceIncludedChoices?: Record<string, string>;
  checkboxes?: string[];
  /**
   * Per-parent nested-checkbox selections: `{[parentOptionKey]:
   * nestedKeys[]}`. Submit flattens parents + nested into a single
   * `optionIds: string[]`.
   */
  checkboxesIncludedChoices?: Record<string, string[]>;
  check?: string;
  files?: RawAttachmentFile[] | AttachmentFile[];
  grid?: TaskGrid | { schema?: IGridSchema };
  /**
   * Definition grid schema (with `columnIds` / `cellIds` threaded by the GET
   * mapper) — the submit mapper uses it to name each answered cell's
   * `columnId` / `gridCellId`.
   */
  gridSchema?: IGridSchema | null;
  /**
   * Custom labels for exhibitor-added grid rows (when the schema has
   * `allowToAddRows`), in add-order. Flattened onto the trailing answer
   * rows' `name` at submit so the labels persist.
   */
  additionalRowNames?: string[];
  signature?: LegacySignatureFormData;
}

export interface LegacySignatureFormData {
  type: SignatureType;
  /** For IMAGE / DRAWING — a fresh blob uploaded before submit, or an
   * already-stored URL (preserved).
   */
  fileBlob?: File | null;
  typedText?: string | null;
  typedFont?: string | null;
  shouldBeShownInList?: boolean;
  /**
   * Id of an existing library signature picked by the organiser. The BE
   * copies the library row's fields into `task_signatures` (copy-on-use
   * so the per-instance row is immutable). Mutually exclusive with
   * `fileBlob` / `typedText`.
   */
  signatureId?: string | null;
}

/**
 * Index a deep-task's options into a `Map<optionKey, optionId>` so
 * MULTIPLE_CHOICE / CHECKBOXES submissions can swap form-state keys for
 * the `task_options.id` UUID the BE expects. Nested options are flattened
 * — sub-keys are unique within a task.
 */
export function buildOptionKeyToIdMap(
  options: IGetOneByIdTaskOption[] | undefined | null
): Map<string, string> {
  const map = new Map<string, string>();
  if (!options) return map;
  for (const o of options) {
    map.set(o.optionKey, o.id);
  }
  return map;
}

/**
 * Load the deep task (cached upstream by react-query when possible) and
 * return a key→id map, for submit helpers that don't already carry one.
 */
async function loadOptionKeyToIdMap(
  taskId: string,
  showId: string
): Promise<Map<string, string>> {
  const deep = await apiV2.task.getOneById({ id: taskId }, { showId });
  return buildOptionKeyToIdMap(deep.options);
}

/**
 * Build the apiV2 per-type submit body from the form-data shape. Throws
 * on unsupported response types (MULTITASK subtasks are submitted
 * individually by the caller).
 */
function buildPerTypeBody(
  responseType: TaskResponseType,
  data: LegacyResponseFormData,
  optionKeyToId: Map<string, string>
): Pick<
  ISubmitTaskInstanceBodyReq,
  | "shortAnswer"
  | "shortAnswerRows"
  | "paragraph"
  | "check"
  | "multipleChoice"
  | "checkboxes"
  | "files"
  | "grid"
> {
  switch (responseType) {
    case TaskResponseType.SHORT_ANSWER:
      return { shortAnswer: { text: data.shortAnswer ?? "" } };
    case TaskResponseType.SHORT_ANSWER_ROWS:
      return {
        shortAnswerRows: {
          rows: (data.shortAnswerRows ?? []).map((text, rowIndex) => ({
            rowIndex,
            text,
          })),
        },
      };
    case TaskResponseType.PARAGRAPH:
      return { paragraph: { text: data.paragraph ?? "" } };
    case TaskResponseType.CHECK:
      return {
        check: {
          // Form state holds "Confirmed" / "Not confirmed" strings; the
          // BE expects a boolean. Anything truthy and not the explicit
          // "Not confirmed" label counts as true.
          isChecked:
            !!data.check &&
            String(data.check).toLowerCase() !== "not confirmed",
        },
      };
    case TaskResponseType.MULTIPLE_CHOICE: {
      const parentKey = data.multipleChoice ?? "";
      // Two-tier: when a nested radio under the picked parent is chosen,
      // persist the deepest pick. The column stores a single id; the
      // renderer walks back up via `parentOptionId` for "Parent → Child".
      const nestedKey = parentKey
        ? data.multipleChoiceIncludedChoices?.[parentKey]
        : undefined;
      const effectiveKey = nestedKey || parentKey;
      const optionId = optionKeyToId.get(effectiveKey);
      if (!optionId) {
        throw new Error(
          `MULTIPLE_CHOICE: no option-id for key "${effectiveKey}"`
        );
      }
      return { multipleChoice: { optionId } };
    }
    case TaskResponseType.CHECKBOXES: {
      // Parents + their per-parent nested picks flatten into one
      // `optionIds` list. The BE stores one row per id; the renderer
      // regroups via `parentOptionId`.
      const parentKeys = data.checkboxes ?? [];
      const nestedMap = data.checkboxesIncludedChoices ?? {};
      const allKeys = new Set<string>();
      for (const k of parentKeys) {
        allKeys.add(k);
        const nested = nestedMap[k];
        if (nested?.length) {
          for (const nk of nested) allKeys.add(nk);
        }
      }
      const optionIds = Array.from(allKeys)
        .map((k) => optionKeyToId.get(k))
        .filter((id): id is string => !!id);
      return { checkboxes: { optionIds } };
    }
    case TaskResponseType.GRID: {
      // Normalized submit: thread each answered cell's `columnId` /
      // `gridCellId` from the definition schema (set on the form by the GET
      // mapper). FILES cells carry storage save-tokens pre-uploaded by
      // `processGrid`; exhibitor-added rows carry their `name` and a null
      // `gridCellId`.
      return {
        grid: formAnswerToSubmitInput(
          data.gridSchema,
          (data.grid ?? []) as GridForm["grid"],
          data.additionalRowNames
        ),
      };
    }
    case TaskResponseType.FILES: {
      // New uploads carry a single-use storage JWT (`token`, set by
      // FileUploadForm after upload). Already-persisted files restored from a
      // prior version / draft carry a `fileId` — send those as `keepFileIds`
      // so the server retains them instead of dropping them (which previously
      // wiped an earlier file when a new one was added).
      const entries = (data.files ?? []) as Array<{
        token?: string;
        fileId?: string;
      }>;
      const files = entries.flatMap((f, orderIndex) =>
        f.token ? [{ token: f.token, orderIndex }] : []
      );
      const keepFileIds = entries.flatMap((f) =>
        !f.token && f.fileId ? [f.fileId] : []
      );
      return { files: { files, keepFileIds } };
    }
    case TaskResponseType.MULTITASK:
      // The parent has no per-type response of its own — subtasks are
      // submitted individually, each with its own `taskId`.
      return {};
    default:
      throw new Error(
        `submit not supported for responseType=${responseType} via the apiV2 helper`
      );
  }
}

/**
 * Pre-upload organiser-side reaction files via the
 * `initReviewerReactionFileUpload` two-token handshake and return the
 * storage JWTs for `reactionFileTokens` on approve / reject / revise /
 * reviewer-decision. Already-uploaded files (no fresh blob) are skipped.
 */
export async function uploadReviewerReactionFiles(
  showId: string,
  files: Array<{ file?: File | { name?: string; size?: number } }>
): Promise<string[]> {
  const tokens: string[] = [];
  for (const f of files) {
    if (!(f.file instanceof File) || f.file.size === 0) continue;
    const {
      token: initToken,
    } = await apiV2.task.initReviewerReactionFileUpload({ showId });
    const { token } = await apiV2.fileUpload.uploadFile({
      // Non-raster attachments (PDF, docx…) pass through untouched.
      file: await compressImage(
        f.file,
        IMAGE_COMPRESSION_MAX_SIZE_MB.taskReview
      ),
      token: initToken,
      requestId: uuid(),
    });
    if (!token) {
      throw new Error("Storage upload returned no token for reviewer file");
    }
    tokens.push(token);
  }
  return tokens;
}

/**
 * Anonymous (email-reviewer) variant of `uploadReviewerReactionFiles`.
 * Each upload init re-validates the HMAC `key` server-side, so a stolen
 * or expired key short-circuits before a storage JWT is minted. Returned
 * tokens go into `decideTaskEmailReview.reactionFileTokens[]`.
 */
export async function uploadEmailReviewerReactionFiles(
  showId: string,
  key: string,
  files: Array<{ file?: File | { name?: string; size?: number } }>
): Promise<string[]> {
  const tokens: string[] = [];
  for (const f of files) {
    if (!(f.file instanceof File) || f.file.size === 0) continue;
    const {
      token: initToken,
    } = await apiV2.task.initEmailReviewReactionFileUpload({ showId, key });
    const { token } = await apiV2.fileUpload.uploadFile({
      file: await compressImage(
        f.file,
        IMAGE_COMPRESSION_MAX_SIZE_MB.taskReview
      ),
      token: initToken,
      requestId: uuid(),
    });
    if (!token) {
      throw new Error(
        "Storage upload returned no token for email-reviewer file"
      );
    }
    tokens.push(token);
  }
  return tokens;
}

/**
 * Pre-upload a signature image (DRAWN / UPLOADED) and produce the
 * `signature` body field. TYPED signatures skip the file flow — the BE
 * persists `typedText` / `typedFont` directly. Returns `undefined` when
 * no signature is provided.
 */
export async function buildSignaturePayload(
  showId: string,
  sig?: LegacySignatureFormData | null
): Promise<ISubmitTaskInstanceSignatureInput | undefined> {
  if (!sig) return undefined;
  // Reuse an existing library signature — the BE copies the row's fields
  // into task_signatures, so no file upload is needed.
  if (sig.signatureId) {
    return {
      signatureType: sig.type,
      signatureId: sig.signatureId,
    };
  }
  let token: string | undefined;
  if (sig.type !== SignatureType.TEXT && sig.fileBlob instanceof File) {
    const {
      token: initToken,
    } = await apiV2.task.initReviewerReactionFileUpload({ showId });
    const upload = await apiV2.fileUpload.uploadFile({
      file: sig.fileBlob,
      token: initToken,
      requestId: uuid(),
    });
    token = upload.token;
  }
  return {
    signatureType: sig.type,
    token,
    typedText: sig.typedText ?? undefined,
    typedFont: sig.typedFont ?? undefined,
    shouldBeShownInList: sig.shouldBeShownInList ?? false,
  };
}

export interface SubmitResponseArgs {
  taskId: string;
  responseType: TaskResponseType;
  isDraft: boolean;
  data: LegacyResponseFormData;
  ctx: ApiV2TaskSubmitContext;
  /**
   * Required by tasks whose definition has `isSignatureRequired = true`.
   * The drawer collects it via the signature modal. For draft saves
   * the BE skips the signature check.
   */
  signature?: LegacySignatureFormData | null;
  /**
   * Pre-built key→id map to avoid a second round-trip when the drawer
   * already loaded the deep task. Optional — the helper falls back to a
   * fresh `getOneById` fetch.
   */
  optionKeyToId?: Map<string, string>;
}

export async function submitResponseViaApiV2(
  args: SubmitResponseArgs
): Promise<ISubmitTaskInstanceRes<string>> {
  const map =
    args.optionKeyToId ??
    (await loadOptionKeyToIdMap(args.taskId, args.ctx.showId));
  const perType = buildPerTypeBody(args.responseType, args.data, map);
  const signature = args.isDraft
    ? undefined
    : await buildSignaturePayload(args.ctx.showId, args.signature);
  return apiV2.task.submitTaskInstance(
    { taskId: args.taskId },
    {
      showId: args.ctx.showId,
      standId: args.ctx.standId,
      exhibitorPairId: args.ctx.exhibitorPairId,
      isDraft: args.isDraft,
      ...perType,
      ...(signature ? { signature } : {}),
    }
  );
}

/** Exhibitor opt-out: "this task does not apply to my stand". */
export async function submitNotApplicableViaApiV2(args: {
  taskId: string;
  ctx: ApiV2TaskSubmitContext;
}): Promise<ISubmitTaskInstanceRes<string>> {
  return apiV2.task.submitTaskInstance(
    { taskId: args.taskId },
    {
      showId: args.ctx.showId,
      standId: args.ctx.standId,
      exhibitorPairId: args.ctx.exhibitorPairId,
      isDraft: false,
      isNotApplicableByExhibitor: true,
    }
  );
}

/** Organiser revise — single-reviewer "needs changes". */
export async function reviseTaskInstanceViaApiV2(args: {
  taskId: string;
  ctx: ApiV2TaskContext;
  message: string;
  files?: Array<{ file?: File | { name?: string; size?: number } }>;
}): Promise<void> {
  const reactionFileTokens = args.files?.length
    ? await uploadReviewerReactionFiles(args.ctx.showId, args.files)
    : undefined;
  const body: IReviseTaskInstanceBodyReq = {
    showId: args.ctx.showId,
    standId: args.ctx.standId,
    message: args.message,
    ...(reactionFileTokens ? { reactionFileTokens } : {}),
  };
  await apiV2.task.reviseTaskInstance({ taskId: args.taskId }, body);
}

/** Organiser approve — single-reviewer task (NOT_REQUIRED / ORGANISER / EMAIL). */
export async function approveTaskInstanceViaApiV2(args: {
  taskId: string;
  ctx: ApiV2TaskContext;
  comment?: string;
  approvalMessage?: string;
  allowResubmitAfterApproval?: boolean;
  files?: Array<{ file?: File | { name?: string; size?: number } }>;
  signature?: LegacySignatureFormData | null;
}): Promise<void> {
  const reactionFileTokens = args.files?.length
    ? await uploadReviewerReactionFiles(args.ctx.showId, args.files)
    : undefined;
  const signature = await buildSignaturePayload(
    args.ctx.showId,
    args.signature
  );
  const body: IApproveTaskInstanceBodyReq = {
    showId: args.ctx.showId,
    standId: args.ctx.standId,
    comment: args.comment,
    approvalMessage: args.approvalMessage,
    allowResubmitAfterApproval: args.allowResubmitAfterApproval,
    ...(reactionFileTokens ? { reactionFileTokens } : {}),
    ...(signature ? { signature } : {}),
  };
  await apiV2.task.approveTaskInstance({ taskId: args.taskId }, body);
}

/** Organiser reject — single-reviewer task. Resets status to TODO + isRejected. */
export async function rejectTaskInstanceViaApiV2(args: {
  taskId: string;
  ctx: ApiV2TaskContext;
  message: string;
  files?: Array<{ file?: File | { name?: string; size?: number } }>;
}): Promise<void> {
  const reactionFileTokens = args.files?.length
    ? await uploadReviewerReactionFiles(args.ctx.showId, args.files)
    : undefined;
  const body: IRejectTaskInstanceBodyReq = {
    showId: args.ctx.showId,
    standId: args.ctx.standId,
    message: args.message,
    ...(reactionFileTokens ? { reactionFileTokens } : {}),
  };
  await apiV2.task.rejectTaskInstance({ taskId: args.taskId }, body);
}

/**
 * Anonymous email-reviewer decide via the magic-link `key`. The BE
 * HMAC-validates the key (with expiry + replay protection) and resolves
 * the acting reviewer; quorum and sequential turn-taking match the
 * organiser-side `reviewer-decision` path. Reject/revise wipes the
 * response; the key stays valid for the reviewer's whole queue but
 * replaying the same decision 409s.
 */
export async function decideTaskEmailReviewViaApiV2(args: {
  showId: string;
  key: string;
  /**
   * Decision target. The key is shared across every task the reviewer is
   * on, so `taskId` + `standId` (from the verify response) pin the
   * verdict to the instance being viewed; without them the BE falls back
   * to the key's FK pointer, ambiguous with several pending tasks.
   */
  taskId?: string;
  standId?: string;
  isApproved: boolean;
  message?: string;
  /** Reject vs Revision discrimination — both keep status=TODO but flip a different flag. */
  isRevision?: boolean;
  /**
   * Optional reaction files attached to the verdict. Each file is
   * pre-uploaded via `uploadEmailReviewerReactionFiles` (anonymous
   * HMAC-gated upload init); the resulting tokens are bundled here
   * for the BE to persist on `task_reviewer_reaction_files`.
   */
  files?: Array<{ file?: File | { name?: string; size?: number } }>;
  /**
   * Optional signature attached to an approve verdict. Same shape as
   * the organiser-side flows. BE lands the row in `task_signatures`
   * keyed by the email reviewer (`signed_by_email`). Silently dropped
   * on `isApproved=false`.
   */
  signature?: LegacySignatureFormData | null;
  /**
   * "Allow exhibitor to resubmit after approval" toggle from the email-review
   * approval modal. Only meaningful on an approve verdict — and only the
   * verdict that flips the instance to DONE lands it on
   * `task_instances.allow_resubmit_after_approval` server-side.
   */
  allowResubmitAfterApproval?: boolean;
}) {
  const reactionFileTokens = args.files?.length
    ? await uploadEmailReviewerReactionFiles(args.showId, args.key, args.files)
    : undefined;
  const signature = args.isApproved
    ? await buildSignaturePayload(args.showId, args.signature)
    : undefined;
  return apiV2.task.decideTaskEmailReview({
    showId: args.showId,
    key: args.key,
    isApproved: args.isApproved,
    ...(args.taskId && args.standId
      ? { taskId: args.taskId, standId: args.standId }
      : {}),
    ...(args.message ? { message: args.message } : {}),
    ...(args.isRevision ? { isRevision: args.isRevision } : {}),
    ...(reactionFileTokens?.length ? { reactionFileTokens } : {}),
    ...(signature ? { signature } : {}),
    // Approve-only, same guard as `reviewerDecisionViaApiV2` below: a reject /
    // revision returns the task to the exhibitor regardless.
    ...(args.isApproved && args.allowResubmitAfterApproval !== undefined
      ? { allowResubmitAfterApproval: args.allowResubmitAfterApproval }
      : {}),
  });
}

/**
 * Multi-reviewer organiser approve / reject. The BE resolves the acting
 * reviewer from the session, enforces sequential turn-taking, applies
 * quorum, and transitions the instance status; reject wipes the response
 * for a clean re-submit. Returns the post-decision `{instance, reviewer}`
 * projection so the caller can detect "last approval → DONE" and trigger
 * the Task PDF print.
 */
export async function reviewerDecisionViaApiV2(args: {
  taskId: string;
  ctx: ApiV2TaskContext;
  isApproved: boolean;
  message?: string;
  allowResubmitAfterApproval?: boolean;
  files?: Array<{ file?: File | { name?: string; size?: number } }>;
  signature?: LegacySignatureFormData | null;
}): Promise<IReviewerDecisionTaskInstanceRes<string>> {
  const reactionFileTokens = args.files?.length
    ? await uploadReviewerReactionFiles(args.ctx.showId, args.files)
    : undefined;
  const signature = await buildSignaturePayload(
    args.ctx.showId,
    args.signature
  );
  const body: IReviewerDecisionTaskInstanceBodyReq = {
    showId: args.ctx.showId,
    standId: args.ctx.standId,
    isApproved: args.isApproved,
    ...(args.message ? { message: args.message } : {}),
    ...(reactionFileTokens ? { reactionFileTokens } : {}),
    ...(args.isApproved && args.allowResubmitAfterApproval !== undefined
      ? { allowResubmitAfterApproval: args.allowResubmitAfterApproval }
      : {}),
    ...(signature ? { signature } : {}),
  };
  return apiV2.task.reviewerDecisionTaskInstance({ taskId: args.taskId }, body);
}

/**
 * Whether the approval-type takes a single-reviewer action
 * (approve/reject); multi-reviewer tasks use `reviewerDecisionViaApiV2`.
 */
export function isSingleReviewerApproval(
  type?: TaskApprovalType | string
): boolean {
  return (
    type === TaskApprovalType.NOT_REQUIRED ||
    type === TaskApprovalType.EMAIL ||
    type === TaskApprovalType.ORGANISER ||
    type === "notRequired" ||
    type === "email" ||
    type === "organiser"
  );
}

/**
 * Pull `instance` from the submit response — the BE returns
 * `{instance, response}` and most callers only need the status/message
 * on `instance`.
 */
export function pickInstanceFromSubmit<D = string>(
  res: ISubmitTaskInstanceRes<D>
): ISubmitTaskInstanceRes<D>["instance"] {
  return res.instance;
}

export type { ITaskResponse };
